import type { Assistant } from '../../services/AssistantService';
import type { AssistantHubItem } from '@/features/assistant-hub';
import type { Bot, ChatRequest, Model } from '../types/api.types';
import type {
  AssistantDetail,
  RecentAssistant,
} from '../types/conversation.types';
import type { DefaultModelDetail } from '../types/assistant.types';

export class AssistantMapper {
  /**
   * Converts DefaultModelDetail (from assistant) to Model type
   */
  private static defaultModelDetailToModel(detail: DefaultModelDetail): Model {
    return {
      id: String(detail.id),
      name: detail.name,
      modelName: detail.modelName,
      modelProvider: detail.modelProvider as
        | 'vertexai'
        | 'anthropic'
        | 'openai',
      description: detail.description,
    };
  }

  /**
   * Safely extracts default_model_detail from assistant object
   * Only Assistant type has default_model_detail, RecentAssistant does not
   */
  private static extractDefaultModelDetail(
    assistant: Assistant | AssistantHubItem | RecentAssistant
  ): Model | null {
    // Check if the assistant has default_model_detail property
    if (
      'default_model_detail' in assistant &&
      assistant.default_model_detail &&
      typeof assistant.default_model_detail === 'object' &&
      'modelName' in assistant.default_model_detail
    ) {
      return this.defaultModelDetailToModel(
        assistant.default_model_detail as DefaultModelDetail
      );
    }
    return null;
  }

  static assistantToBot(
    assistant: Assistant | AssistantHubItem | RecentAssistant
  ): Bot {
    const baseAssistant = 'isFavourite' in assistant ? assistant : assistant;

    return {
      _id: String(baseAssistant.id),
      // Preserve uid for proper assistant identification in URL comparisons
      uid: baseAssistant.uid,
      bot_name: baseAssistant.bot_name,
      description: baseAssistant.description,
      division: baseAssistant.division || '',
      workspace: baseAssistant.workspace || '',
      datastore: Array.isArray(baseAssistant.datastore)
        ? baseAssistant.datastore
        : baseAssistant.datastore
          ? [baseAssistant.datastore]
          : [],
      owner_email: baseAssistant.owner_email || '',
      last_updated: baseAssistant.last_updated || '',
      type: baseAssistant.type,
      sop: baseAssistant.sop || '',
      faqs: baseAssistant.faqs || [],
      groups: baseAssistant.groups || [],
      k_value: baseAssistant.k_value || 5,
      temperature: baseAssistant.temperature || 0.7,
      system_instructions:
        baseAssistant.system_instructions || 'You are a helpful assistant.',
      tags: [],
      created_at: baseAssistant.date_added,
      updated_at: baseAssistant.last_updated,
      is_default:
        'is_default' in baseAssistant
          ? Boolean(
              (
                baseAssistant as {
                  is_default?: boolean | null | undefined;
                }
              ).is_default
            )
          : false,
      department:
        typeof baseAssistant.department === 'object' &&
        baseAssistant.department !== null &&
        'id' in baseAssistant.department
          ? (baseAssistant.department as { id: number }).id
          : typeof baseAssistant.department === 'number'
            ? baseAssistant.department
            : undefined,
      // Preserve default model configuration
      default_model: baseAssistant.default_model ?? null,
      default_model_detail: this.extractDefaultModelDetail(baseAssistant),
    };
  }

  static assistantDetailToBot(assistantDetail: AssistantDetail): Bot {
    // Handle datastore which can be either string or string[]
    const datastoreArray = Array.isArray(assistantDetail.datastore)
      ? assistantDetail.datastore
      : assistantDetail.datastore
        ? [assistantDetail.datastore]
        : [];

    return {
      _id: String(assistantDetail.id),
      // Preserve uid for proper assistant identification in URL comparisons
      uid: assistantDetail.uid,
      bot_name: assistantDetail.bot_name,
      description: assistantDetail.description,
      division: assistantDetail.division || '',
      workspace: assistantDetail.workspace || '',
      datastore: datastoreArray,
      owner_email: assistantDetail.owner_email,
      last_updated: assistantDetail.last_updated,
      type: assistantDetail.type,
      sop: assistantDetail.sop || '',
      faqs: assistantDetail.faqs || [],
      groups: assistantDetail.groups || [],
      k_value: assistantDetail.k_value || 5,
      temperature: assistantDetail.temperature || 0.7,
      system_instructions:
        assistantDetail.system_instructions || 'You are a helpful assistant.',
      tags: [],
      created_at: assistantDetail.date_added,
      updated_at: assistantDetail.last_updated,
      department: assistantDetail.department || undefined,
      is_default:
        'is_default' in assistantDetail
          ? Boolean(
              (
                assistantDetail as {
                  is_default?: boolean | null | undefined;
                }
              ).is_default
            )
          : false,
      // Preserve default model configuration (AssistantDetail only has default_model, not full detail)
      default_model: assistantDetail.default_model ?? null,
      default_model_detail: null, // AssistantDetail doesn't include full model detail
    };
  }

  static assistantToChatConfig(
    assistant: Assistant | AssistantHubItem | RecentAssistant
  ): Partial<ChatRequest['config']> {
    // rag_enabled should be true only when assistant exists and is not a default bot
    const ragEnabled = Boolean(
      assistant && 'is_default' in assistant && !assistant.is_default
    );
    return {
      k: assistant.k_value || 5,
      temperature: assistant.temperature || 0.7,
      system_prompt:
        assistant.system_instructions || 'You are a helpful assistant.',
      store_ids: Array.isArray(assistant.datastore)
        ? assistant.datastore
        : assistant.datastore
          ? [assistant.datastore]
          : [],
      rag_enabled: ragEnabled,
      web_search_enabled: false,
    };
  }

  static prepareChatRequestFromAssistant(
    assistant: Assistant | AssistantHubItem | RecentAssistant,
    question: string,
    conversationId: string,
    modelProvider: string,
    modelName: string,
    webSearchEnabled: boolean = false
  ): ChatRequest {
    const chatConfig = this.assistantToChatConfig(assistant);

    return {
      question,
      config: {
        model_provider: modelProvider,
        model_name: modelName,
        conversation_id: conversationId,
        web_search_enabled: webSearchEnabled,
        ...chatConfig,
      },
    };
  }

  static isAssistant(data: unknown): data is Assistant {
    return (
      typeof data === 'object' &&
      data !== null &&
      'id' in data &&
      typeof (data as Record<string, unknown>).id === 'number' &&
      'bot_name' in data &&
      'owner_email' in data
    );
  }

  static isAssistantHubItem(data: unknown): data is AssistantHubItem {
    return (
      this.isAssistant(data) &&
      'isFavourite' in data &&
      'isOwned' in data &&
      'userRole' in data
    );
  }

  static isBot(data: unknown): data is Bot {
    return (
      typeof data === 'object' &&
      data !== null &&
      '_id' in data &&
      typeof (data as Record<string, unknown>)._id === 'string' &&
      'bot_name' in data &&
      'owner_email' in data
    );
  }
}

export const assistantMapper = new AssistantMapper();
